/**
 * WebSocket Debug Utilities
 * 
 * Comprehensive debug logging and payload normalization for WebSocket device updates.
 * Handles multiple payload formats from different device types.
 * 
 * @module websocketDebug
 */

// Enable debug mode via environment variable or localStorage
const getDebugEnabled = (): boolean => {
  if (typeof window !== 'undefined') {
    // Check localStorage for runtime toggle
    const storedDebug = localStorage.getItem('ws_debug');
    if (storedDebug === 'true') return true;
  }
  // Check environment variable
  return process.env.REACT_APP_WS_DEBUG === 'true' || 
         process.env.REACT_APP_DEBUG === 'true';
};

// Payload format types observed in production
export type WebSocketPayloadFormat = 
  | 'delta'           // New format: { data: { deviceSN, delta: {...} } }
  | 'iotState'        // Current format: { data: { deviceSN, iotState: {...} } }
  | 'statesData'      // Legacy format: { data: { sn, statesData: {...} } }
  | 'multiEndpoint'   // Multi-endpoint: { data: { baseSN, endpoint, delta: {...} } }
  | 'state'           // Simple: { data: { deviceSN, state: {...} } }
  | 'unknown';

// Device types for categorization
export type DeviceType = 
  | 'hvac' | 'light' | 'switch' | 'fan' | 'outlet'
  | 'power_meter' | 'th_sensor' | 'presence_sensor' 
  | 'motion_sensor' | 'door_sensor' | 'curtain' | 'blind' | 'button'
  | 'unknown';

// Normalized WebSocket update
export interface NormalizedWebSocketUpdate {
  // Device identification
  deviceSN: string;           // Full device SN (including #endpoint if applicable)
  baseSN: string;             // Base SN without endpoint
  endpoint?: string;          // Endpoint number (e.g., "1", "2")
  
  // State data
  iotState: Record<string, any>;  // Normalized state data
  
  // Metadata
  timestamp?: number;         // WebSocket timestamp (if available)
  receivedAt: number;         // Local receive time
  online?: boolean;           // Online status
  
  // Debug info
  rawPayload: any;            // Original payload for debugging
  payloadFormat: WebSocketPayloadFormat;
  detectedDeviceType?: DeviceType;
}

// Pending command state for stale data filtering
// Supports both 'commandTime' (new) and 'timestamp' (legacy) field names
export interface PendingCommandState {
  expectedState: {
    on?: boolean;
    brightness?: number;
    fanSpeed?: string;
    thermostatMode?: string;
    thermostatTemperatureSetpoint?: number;
  };
  commandTime?: number;       // When command was issued (new name)
  timestamp?: number;         // When command was issued (legacy name)
  updateCount: number;        // Count of updates received
  acceptedOnce: boolean;      // Whether matching state was received
  sequenceId?: number;        // Optional sequence tracking
  lastUpdateTime?: number;    // Last update timestamp
  lastUpdateTimestamp?: number; // Legacy name for lastUpdateTime
}

/**
 * Log WebSocket debug information
 */
export function logWebSocketDebug(
  event: string,
  data: any,
  context?: Record<string, any>
): void {
  if (!getDebugEnabled()) return;
  
  const timestamp = new Date().toISOString();
  const prefix = `🔍 [WS DEBUG ${timestamp}]`;
  
  console.group(`${prefix} ${event}`);
  console.log('📦 Full Payload:', JSON.stringify(data, null, 2));
  
  if (context) {
    console.log('📋 Context:', context);
  }
  
  // Analyze payload structure
  const analysis = analyzePayload(data);
  console.log('🔬 Analysis:', analysis);
  
  console.groupEnd();
}

/**
 * Analyze WebSocket payload structure
 */
function analyzePayload(data: any): Record<string, any> {
  const analysis: Record<string, any> = {
    hasDeviceSN: !!data?.deviceSN,
    hasBaseSN: !!data?.baseSN,
    hasSN: !!data?.sn,
    hasEndpoint: !!data?.endpoint,
    hasDelta: !!data?.delta,
    hasIotState: !!data?.iotState,
    hasState: !!data?.state,
    hasStatesData: !!data?.statesData,
    hasAttributes: !!data?.attributes,
    hasTimestamp: !!data?.timestamp,
    hasOnline: data?.online !== undefined,
    topLevelKeys: data ? Object.keys(data) : [],
  };
  
  // Detect payload format
  analysis.detectedFormat = detectPayloadFormat(data);
  
  // Extract state keys if available
  const stateData = data?.delta || data?.iotState || data?.state || data?.statesData;
  if (stateData && typeof stateData === 'object') {
    analysis.stateKeys = Object.keys(stateData);
    // Check for nested endpoint data
    if (stateData['1'] || stateData['2'] || stateData['3']) {
      analysis.hasNestedEndpoints = true;
      analysis.nestedEndpoints = Object.keys(stateData).filter(k => /^\d+$/.test(k));
    }
  }
  
  return analysis;
}

/**
 * Detect the payload format type
 */
function detectPayloadFormat(data: any): WebSocketPayloadFormat {
  if (!data) return 'unknown';
  
  // Check for multi-endpoint format first
  if (data.baseSN && data.endpoint && (data.delta || data.iotState)) {
    return 'multiEndpoint';
  }
  
  // Check for delta format (new)
  if (data.delta && typeof data.delta === 'object') {
    return 'delta';
  }
  
  // Check for iotState format (current)
  if (data.iotState && typeof data.iotState === 'object') {
    return 'iotState';
  }
  
  // Check for state format (simple)
  if (data.state && typeof data.state === 'object') {
    return 'state';
  }
  
  // Check for legacy statesData format
  if (data.statesData && typeof data.statesData === 'object') {
    return 'statesData';
  }
  
  // Check if data itself is the state (no wrapper)
  if (data.on !== undefined || data.brightness !== undefined || 
      data.temp !== undefined || data.watt !== undefined) {
    return 'delta';  // Treat as delta if it looks like state data
  }
  
  return 'unknown';
}

/**
 * Detect device type from SN pattern or state data
 */
function detectDeviceType(sn: string, stateData?: Record<string, any>): DeviceType {
  const snUpper = sn.toUpperCase();
  
  // SN pattern detection
  if (snUpper.includes('UT3M15')) return 'power_meter';
  if (snUpper.includes('UTU32A')) return 'presence_sensor';  // Multi-sensor
  if (snUpper.includes('UTBM100')) return 'presence_sensor'; // mmWave sensor
  if (snUpper.includes('UTZ200')) return 'th_sensor';        // TH sensor
  if (snUpper.includes('UTASL') || snUpper.includes('UTL')) return 'light';
  if (snUpper.includes('UTACP') || snUpper.includes('UTC')) return 'hvac';
  if (snUpper.includes('UTASW') || snUpper.includes('UTS')) return 'switch';
  if (snUpper.includes('UTAFN') || snUpper.includes('UTF')) return 'fan';
  if (snUpper.includes('UTADC') || snUpper.includes('UTD')) {
    if (stateData?.detected !== undefined) return 'door_sensor';
    return 'door_sensor';
  }
  if (snUpper.includes('UTAMS') || snUpper.includes('UTM')) return 'motion_sensor';
  if (snUpper.includes('UTACR') || snUpper.includes('UTABL')) return 'curtain';
  if (snUpper.includes('UTABK') || snUpper.includes('UTABN')) return 'button';
  
  // State-based detection if SN pattern not recognized
  if (stateData) {
    if (stateData.thermostatMode !== undefined) return 'hvac';
    if (stateData.brightness !== undefined) return 'light';
    if (stateData.fanSpeed !== undefined && stateData.thermostatMode === undefined) return 'fan';
    if (stateData.watt !== undefined || stateData.real_power !== undefined) return 'power_meter';
    if (stateData.temp !== undefined || stateData.rh !== undefined) return 'th_sensor';
    if (stateData.presence !== undefined || stateData.occupancy !== undefined) return 'presence_sensor';
    if (stateData.motionDetected !== undefined || stateData.motion !== undefined) return 'motion_sensor';
    if (stateData.openPercent !== undefined) return 'curtain';
    if (stateData.detected !== undefined) {
      const detectedValue = stateData.detected?.value ?? stateData.detected;
      if (detectedValue === 'opened' || detectedValue === 'closed') return 'door_sensor';
      if (detectedValue === 'motionDetected') return 'motion_sensor';
    }
  }
  
  return 'unknown';
}

/**
 * Normalize WebSocket payload to a consistent format
 * This is the main function to use - it handles ALL known payload formats
 */
export function normalizeWebSocketPayload(rawData: any): NormalizedWebSocketUpdate | null {
  const receivedAt = Date.now();
  
  // Handle array payloads (webhook:event can be array)
  const data = Array.isArray(rawData) ? rawData[0] : rawData;
  if (!data) return null;
  
  // Handle nested data structure (webhook:event format)
  const eventData = data.data || data;
  
  // Extract device identification
  const rawSN = eventData.deviceSN || eventData.sn || eventData.baseSN || eventData.device;
  if (!rawSN) {
    if (getDebugEnabled()) {
      console.warn('⚠️ [WS Normalize] No device SN found in payload:', data);
    }
    return null;
  }
  
  // Handle endpoint extraction
  const alreadyHasEndpoint = rawSN.includes('#');
  const baseSN = alreadyHasEndpoint ? rawSN.split('#')[0] : rawSN;
  const endpointFromSN = alreadyHasEndpoint ? rawSN.split('#')[1] : undefined;
  const endpoint = endpointFromSN || eventData.endpoint;
  const deviceSN = alreadyHasEndpoint 
    ? rawSN 
    : (endpoint ? `${baseSN}#${endpoint}` : baseSN);
  
  // Detect payload format
  const payloadFormat = detectPayloadFormat(eventData);
  
  // Extract state data based on format
  let iotState: Record<string, any> = {};
  
  // Metadata keys that should NOT be treated as state data
  const metadataKeys = ['deviceSN', 'sn', 'baseSN', 'device', 'endpoint', 'timestamp', 'receivedAt', 'online', 'type', 'event'];
  
  switch (payloadFormat) {
    case 'delta':
      // Check if delta exists as a wrapper, otherwise use eventData directly (unwrapped format)
      if (eventData.delta && typeof eventData.delta === 'object') {
        iotState = eventData.delta;
      } else {
        // Payload is the state itself - extract all non-metadata properties
        // This handles formats like: { deviceSN: "xxx", on: true, currentModeSettings: {...} }
        iotState = {};
        for (const [key, value] of Object.entries(eventData)) {
          if (!metadataKeys.includes(key)) {
            iotState[key] = value;
          }
        }
      }
      break;
    case 'iotState':
      iotState = eventData.iotState || {};
      break;
    case 'state':
      iotState = eventData.state || {};
      break;
    case 'statesData':
      iotState = eventData.statesData || {};
      break;
    case 'multiEndpoint':
      iotState = eventData.delta || eventData.iotState || {};
      break;
    default:
      // Extract ALL non-metadata properties as state data
      // This ensures we don't miss any device-specific state fields
      iotState = {};
      for (const [key, value] of Object.entries(eventData)) {
        if (!metadataKeys.includes(key) && value !== undefined) {
          iotState[key] = value;
        }
      }
  }
  
  // Flatten nested endpoint data if present (e.g., iotState["1"])
  // Only do this if endpoint is specified
  if (endpoint && iotState[endpoint] && typeof iotState[endpoint] === 'object') {
    iotState = { ...iotState, ...iotState[endpoint] };
  }
  
  // Also check for state data in eventData.iotState[endpoint]
  if (endpoint && eventData.iotState?.[endpoint]) {
    iotState = { ...iotState, ...eventData.iotState[endpoint] };
  }
  
  // Extract timestamp - check multiple sources
  // Priority: eventData.timestamp > eventData.receivedAt > data.timestamp > iotState.timestamp
  let timestamp: number | undefined;
  const tsValue = eventData.timestamp || eventData.receivedAt || data.timestamp || iotState?.timestamp;
  if (tsValue) {
    if (typeof tsValue === 'string') {
      const parsed = new Date(tsValue).getTime();
      if (!isNaN(parsed)) timestamp = parsed;
    } else if (typeof tsValue === 'number') {
      timestamp = tsValue;
    }
  }
  // If still no timestamp, use receivedAt as fallback for timestamp comparison
  // This helps with optimistic lock when server doesn't provide timestamp
  if (timestamp === undefined) {
    timestamp = receivedAt;
  }
  
  // Detect device type
  const detectedDeviceType = detectDeviceType(baseSN, iotState);
  
  const normalized: NormalizedWebSocketUpdate = {
    deviceSN,
    baseSN,
    endpoint,
    iotState,
    timestamp,
    receivedAt,
    online: eventData.online,
    rawPayload: rawData,
    payloadFormat,
    detectedDeviceType,
  };
  
  if (getDebugEnabled()) {
    logWebSocketDebug('Normalized Payload', normalized, {
      originalFormat: payloadFormat,
      deviceType: detectedDeviceType,
      stateKeys: Object.keys(iotState),
    });
  }
  
  return normalized;
}

/**
 * Check if an update should be filtered (stale data protection)
 * Returns true if the update should be ACCEPTED, false if it should be FILTERED
 */
export function shouldAcceptUpdate(
  pending: PendingCommandState | undefined,
  normalizedUpdate: NormalizedWebSocketUpdate,
  getArrayValue: (arr: any, fallback: any) => any
): { accept: boolean; reason: string } {
  // No pending command - always accept
  if (!pending) {
    return { accept: true, reason: 'no_pending_command' };
  }
  
  const now = Date.now();
  // Support both 'commandTime' and 'timestamp' field names
  const cmdTime = pending.commandTime ?? pending.timestamp ?? now;
  const timeSinceCommand = now - cmdTime;
  
  // Strategy A: Timestamp comparison
  // If WebSocket timestamp is newer than command time, accept
  if (normalizedUpdate.timestamp && normalizedUpdate.timestamp > cmdTime) {
    return { 
      accept: true, 
      reason: `timestamp_newer (ws: ${normalizedUpdate.timestamp} > cmd: ${cmdTime})` 
    };
  }
  
  // Strategy B: Timeout (extended to 8 seconds for slow devices)
  const STALE_FILTER_TIMEOUT = 8000;
  if (timeSinceCommand > STALE_FILTER_TIMEOUT) {
    return { 
      accept: true, 
      reason: `timeout_reached (${timeSinceCommand}ms > ${STALE_FILTER_TIMEOUT}ms)` 
    };
  }
  
  const { iotState } = normalizedUpdate;
  
  // Strategy C: State matches expectation
  // Check 'on' state
  if (pending.expectedState.on !== undefined && iotState.on !== undefined) {
    const incomingOn = getArrayValue(iotState.on, undefined);
    const incomingOnBool = incomingOn === true || incomingOn === 'true' || incomingOn === 1;
    
    if (incomingOnBool === pending.expectedState.on) {
      return { 
        accept: true, 
        reason: `on_state_matches (expected: ${pending.expectedState.on}, got: ${incomingOnBool})` 
      };
    }
  }
  
  // Check fanSpeed
  if (pending.expectedState.fanSpeed !== undefined) {
    let incomingFanSpeed: string | undefined;
    if (iotState.fanSpeed !== undefined) {
      incomingFanSpeed = String(getArrayValue(iotState.fanSpeed, undefined));
    } else if (iotState.currentModeSettings?.FanSpeed !== undefined) {
      incomingFanSpeed = String(iotState.currentModeSettings.FanSpeed);
    }
    
    if (incomingFanSpeed === pending.expectedState.fanSpeed) {
      return { 
        accept: true, 
        reason: `fanSpeed_matches (expected: ${pending.expectedState.fanSpeed}, got: ${incomingFanSpeed})` 
      };
    }
  }
  
  // Check thermostat settings
  if (pending.expectedState.thermostatTemperatureSetpoint !== undefined) {
    const incomingTemp = iotState.thermostatTemperatureSetpoint ?? 
                         iotState.thermostatTemperatureAmbient;
    if (incomingTemp !== undefined && 
        Math.abs(Number(incomingTemp) - pending.expectedState.thermostatTemperatureSetpoint) < 0.5) {
      return { 
        accept: true, 
        reason: `temperature_matches (expected: ${pending.expectedState.thermostatTemperatureSetpoint}, got: ${incomingTemp})` 
      };
    }
  }
  
  // Strategy D: Sequence tracking - already accepted matching once
  if (pending.acceptedOnce) {
    return { 
      accept: true, 
      reason: 'already_accepted_once' 
    };
  }
  
  // Strategy E: Update count - if 3+ conflicting updates, accept
  // Increased from 2 to 3 to prevent on-off-on flicker from burst of stale updates
  // (this indicates device may be unresponsive or command failed)
  const UPDATE_COUNT_THRESHOLD = 3;
  if (pending.updateCount >= UPDATE_COUNT_THRESHOLD) {
    return { 
      accept: true, 
      reason: `update_count_exceeded (count: ${pending.updateCount} >= ${UPDATE_COUNT_THRESHOLD})` 
    };
  }
  
  // Check if update contains our expected state property at all
  // If not (e.g., sensor data, power readings), don't filter
  const hasRelevantState = 
    (pending.expectedState.on !== undefined && iotState.on !== undefined) ||
    (pending.expectedState.fanSpeed !== undefined && 
      (iotState.fanSpeed !== undefined || iotState.currentModeSettings?.FanSpeed !== undefined)) ||
    (pending.expectedState.brightness !== undefined && iotState.brightness !== undefined);
  
  if (!hasRelevantState) {
    return { 
      accept: true, 
      reason: 'no_relevant_state_in_update' 
    };
  }
  
  // Filter - state conflicts with expectation
  return { 
    accept: false, 
    reason: `state_conflicts (on: expected=${pending.expectedState.on}, got=${iotState.on})` 
  };
}

/**
 * Log stale data filtering decision
 * Accepts any pending state object with either 'commandTime' or 'timestamp' field
 */
export function logFilterDecision(
  deviceId: string,
  decision: { accept: boolean; reason: string },
  pending?: { expectedState?: any; commandTime?: number; timestamp?: number; updateCount?: number; acceptedOnce?: boolean }
): void {
  if (!getDebugEnabled()) return;
  
  const icon = decision.accept ? '✅' : '🛡️';
  const action = decision.accept ? 'ACCEPT' : 'FILTER';
  
  // Support both 'commandTime' and 'timestamp' field names
  const cmdTime = pending?.commandTime ?? pending?.timestamp;
  
  console.log(
    `${icon} [WS Filter] ${action} update for ${deviceId}: ${decision.reason}`,
    pending ? {
      expectedState: pending.expectedState,
      timeSinceCommand: cmdTime ? Date.now() - cmdTime : 'N/A',
      updateCount: pending.updateCount,
      acceptedOnce: pending.acceptedOnce,
    } : {}
  );
}

/**
 * Enable/disable WebSocket debug mode at runtime
 */
export function setWebSocketDebugEnabled(enabled: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('ws_debug', enabled ? 'true' : 'false');
    console.log(`🔍 [WS Debug] ${enabled ? 'Enabled' : 'Disabled'}`);
  }
}

/**
 * Get current debug status
 */
export function isWebSocketDebugEnabled(): boolean {
  return getDebugEnabled();
}

// Expose debug toggle to window for runtime access
if (typeof window !== 'undefined') {
  (window as any).wsDebug = {
    enable: () => setWebSocketDebugEnabled(true),
    disable: () => setWebSocketDebugEnabled(false),
    status: () => isWebSocketDebugEnabled(),
  };
}

const websocketDebugUtils = {
  normalizeWebSocketPayload,
  shouldAcceptUpdate,
  logWebSocketDebug,
  logFilterDecision,
  setWebSocketDebugEnabled,
  isWebSocketDebugEnabled,
};

export default websocketDebugUtils;

